/**
 * =============================================================================
 * IDS CRM Web - useTemplateCheckForm hook
 * =============================================================================
 *
 * PURPOSE:
 * Shared state machine for the check-form template lifecycle, previously
 * duplicated five times (TicketDetail brush/spray/generic, TaskDetail,
 * MaintenanceTaskDetail). One hook instance manages one check "slot":
 *
 *   - initial resolve via /api/v1/check-form-templates/resolve when enabled;
 *   - restore from a saved snapshot (edit flows) via the caller's loadSaved;
 *   - full state reset + fresh resolve when the resolve params change
 *     (work type change), skipping the saved snapshot after a manual change
 *     when skipSavedAfterParamsChange is set;
 *   - request-id guard so a superseded resolve/fallback response can never
 *     overwrite newer state;
 *   - optional second targeting (resolveFallbackParams): a 404 on the first
 *     /resolve resolves again with it before anything below applies (a
 *     Quarterly Health Check job tries its scheduled-maintenance form, then
 *     the selected work type's);
 *   - 404 => no template (failed, completion not blocked). With the site_work
 *     fallback chain configured, 404/409/error on /resolve falls back to
 *     /check-form-templates/active/:formType instead;
 *   - 409 => resolveWarning set + failed (completion blocked at call sites);
 *   - answers + per-field ("template:field_key") attachment state and payload
 *     builders matching what the save endpoints expect today.
 *
 * Saved Log Work (Phase 6 step 7, spec 6.5): an optional uploadAttachment
 * sends each picked photo into the job's saved Log Work one at a time, and
 * the slot keeps the file reference the server gives back (a url, never the
 * photo itself), so Complete Work Log sends links instead of base64. Without it
 * nothing changes: tickets and maintenance sign-off still read the photos
 * into data URLs as before.
 *
 * =============================================================================
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { filesToAttachmentPayload, validateAttachment } from '../utils/attachments';

/**
 * The attachment a slot keeps for a photo already sent into the saved Log
 * Work: only the file's own fields, so nothing screen-only (a preview, a
 * send state) is ever filed with the check. A reference without a url is
 * still waiting to send; client_ref ties it to the file once it arrives.
 */
export function attachmentFromFileRef(ref, stepKey) {
  const source = ref && typeof ref === 'object' ? ref : {};
  const attachment = {};
  ['file_id', 'client_ref', 'attachment_id', 'name', 'size', 'type', 'url', 'uploaded_at'].forEach(field => {
    if (source[field] !== undefined && source[field] !== null) attachment[field] = source[field];
  });
  if (stepKey !== undefined) attachment.step_key = stepKey;
  return attachment;
}

/**
 * Fills in the link of each photo that was still sending (a client_ref and no
 * url) from the saved Log Work's files, matched by client_ref. Returns the
 * same array when nothing changed. Complete Work Log sends only links, so the
 * Log Work modal runs the van check's damage photos through this before it
 * builds the body; a check form section does it by itself.
 */
export function linkSentAttachments(attachments, files) {
  const list = Array.isArray(attachments) ? attachments : [];
  const byRef = new Map((Array.isArray(files) ? files : [])
    .filter(file => file && file.client_ref && file.url)
    .map(file => [file.client_ref, file]));
  if (byRef.size === 0) return attachments;
  let changed = false;
  const linked = list.map(item => {
    const file = item && item.client_ref ? byRef.get(item.client_ref) : null;
    if (!file || (item.url === file.url && item.file_id === file.file_id)) return item;
    changed = true;
    return attachmentFromFileRef({ ...item, ...file }, item.step_key);
  });
  return changed ? linked : attachments;
}

const REMOVE_FAILED_MESSAGE = 'The photo could not be removed. Try again.';

/**
 * Takes a sent photo out of the saved Log Work through remove(attachment).
 * remove may throw, or resolve with useSavedLogWork's { ok, message }: either
 * refusal goes to onFailed(sentence). Never throws.
 */
export function removeSentAttachment(remove, attachment, onFailed) {
  return Promise.resolve()
    .then(() => remove(attachment))
    .then(result => {
      if (result && result.ok === false && !result.unavailable) onFailed(result.message || REMOVE_FAILED_MESSAGE);
    })
    .catch(err => onFailed((err && err.message) || REMOVE_FAILED_MESSAGE));
}

/**
 * Sends picked files one at a time through upload(file), in the order picked.
 * A file the picker rules refuse, or one the upload refuses, is named in the
 * returned errors; the others still go. onSent(ref) runs for each file sent.
 *
 * upload may resolve with the FileRef itself, or with useSavedLogWork's
 * uploadFile answer { ok, ref, message }: ok false is a refusal with that
 * message, and ref is the FileRef (a photo still waiting to send has a
 * client_ref and no url yet).
 */
export async function uploadFilesOneByOne(files, upload, onSent) {
  const errors = [];
  for (const file of Array.from(files || [])) {
    const refused = validateAttachment(file);
    if (refused) {
      errors.push(refused);
      continue;
    }
    const couldNotSend = `"${file && file.name}" could not be added. Try adding it again.`;
    try {
      // eslint-disable-next-line no-await-in-loop
      const result = await upload(file);
      if (result && result.ok === false) {
        errors.push(result.message || couldNotSend);
      } else {
        const ref = result && result.ref ? result.ref : result;
        if (ref) onSent(ref);
      }
    } catch (err) {
      errors.push((err && err.message) || couldNotSend);
    }
  }
  return errors;
}

/**
 * Serialises the values that should force a full reset + fresh resolve when
 * they change (the resolve targeting params plus an optional caller-supplied
 * reset signal, e.g. the selected work type for params that don't include it).
 */
const resetKeyOf = (resolveParams, resetSignal, resolveFallbackParams) => JSON.stringify([
  resetSignal === undefined ? null : resetSignal,
  Object.entries(resolveParams || {}),
  Object.entries(resolveFallbackParams || {})
]);

/**
 * @param {object} options
 * @param {Function} options.authFetch          Authenticated fetch from AuthContext.
 * @param {boolean}  options.enabled            Whether this slot is active at all.
 * @param {object}   options.resolveParams      Query params for /resolve
 *                                              (category, workLogType, assetType,
 *                                              maintenanceType?, serviceYear?,
 *                                              pipelineStageProduct?, pipelineStageName?).
 * @param {object}   [options.resolveFallbackParams]
 *                                              Second /resolve params, used only when the
 *                                              first /resolve returns 404. usedResolveFallback
 *                                              reports whether the template came from them.
 * @param {*}        [options.resetSignal]      Extra value that forces a reset +
 *                                              re-resolve when it changes even if
 *                                              resolveParams are unchanged.
 * @param {Function} [options.loadSaved]        Optional (async) () => saved state or null.
 *                                              Saved shape: { template, metadata?, answers,
 *                                              attachments, completed?, savedCheck? }.
 * @param {boolean}  [options.skipSavedAfterParamsChange]
 *                                              Skip loadSaved once the params have been
 *                                              manually changed (brush/spray behaviour).
 * @param {object}   [options.fallback]         { formType } - enables the legacy site_work
 *                                              fallback chain to /active/:formType.
 * @param {boolean}  [options.resetOnDisabled]  Clear all state while enabled is false
 *                                              (generic-check behaviour).
 * @param {Function} [options.conflictMessage]  (errorBody, targeting) => warning string for a
 *                                              /resolve 409 outside the fallback chain.
 * @param {Function} [options.fallbackConflictMessage]
 *                                              (errorBody) => warning string for a 409
 *                                              from the /active/:formType fallback.
 * @param {Function} [options.onError]          (message) => void for attachment errors.
 * @param {Function} [options.onParamsChangeReset]
 *                                              Called after a params-change reset so the
 *                                              caller can clear its own related state.
 * @param {string}   [options.logLabel]         Console log prefix.
 * @param {Function} [options.uploadAttachment] Saved Log Work only: (stepKey, file) => Promise<FileRef>.
 *                                              Each picked file is sent on its own and the slot
 *                                              keeps attachmentFromFileRef(ref, stepKey). A
 *                                              rejection's message goes to onError. Absent: the
 *                                              photos are read into data URLs, as before.
 * @param {Function} [options.removeUploadedAttachment]
 *                                              Saved Log Work only: (attachment) => Promise. Called
 *                                              when a sent photo (one with a file_id or client_ref)
 *                                              is removed, so it leaves the saved Log Work too.
 */
function useTemplateCheckForm({
  authFetch,
  enabled,
  resolveParams,
  resolveFallbackParams = null,
  resetSignal,
  loadSaved,
  skipSavedAfterParamsChange = false,
  fallback = null,
  resetOnDisabled = false,
  conflictMessage,
  fallbackConflictMessage,
  onError,
  onParamsChangeReset,
  logLabel = 'Template Check',
  uploadAttachment = null,
  removeUploadedAttachment = null
}) {
  const [template, setTemplate] = useState(null);
  const [metadata, setMetadata] = useState(null); // { template_id, template_version, template_snapshot }
  const [answers, setAnswers] = useState({});
  const [attachments, setAttachments] = useState({}); // { 'template:field_key': [attachments] }
  const [resolveWarning, setResolveWarning] = useState(null);
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [savedCheck, setSavedCheck] = useState(null); // full saved check row (generic edit flow)
  const [usedResolveFallback, setUsedResolveFallback] = useState(false);

  // Guards against a slow/superseded /resolve or fallback response overwriting
  // state set by a later request (or set after a reset).
  const requestIdRef = useRef(0);
  // True once the params have been manually changed - skips saved-snapshot
  // restore so a genuinely new work type always gets a fresh /resolve.
  const manualChangeRef = useRef(false);

  const resetKey = resetKeyOf(resolveParams, resetSignal, resolveFallbackParams);
  const [prevResetKey, setPrevResetKey] = useState(resetKey);
  const prevEnabledRef = useRef(enabled);

  const reset = useCallback(() => {
    requestIdRef.current += 1;
    setTemplate(null);
    setMetadata(null);
    setAnswers({});
    setAttachments({});
    setResolveWarning(null);
    setFailed(false);
    setLoading(false);
    setCompleted(false);
    setSavedCheck(null);
    setUsedResolveFallback(false);
  }, []);

  // Full state reset + fresh resolve when the resolve params change, so the
  // effect below re-resolves for the newly-selected work type instead of
  // leaving the previous type's template on screen.
  useEffect(() => {
    if (resetKey !== prevResetKey) {
      setPrevResetKey(resetKey);
      if (enabled) {
        if (skipSavedAfterParamsChange) {
          manualChangeRef.current = true;
        }
        reset();
        if (onParamsChangeReset) onParamsChangeReset();
      }
    }
  }, [resetKey, prevResetKey, enabled, skipSavedAfterParamsChange, reset, onParamsChangeReset]);

  // Re-resolve fresh each time the slot is re-enabled (e.g. a sign-off modal
  // reopened), and optionally clear state while disabled.
  useEffect(() => {
    if (enabled && !prevEnabledRef.current) {
      reset();
    }
    prevEnabledRef.current = enabled;
  }, [enabled, reset]);

  useEffect(() => {
    if (!enabled && resetOnDisabled && (template || resolveWarning)) {
      reset();
    }
  }, [enabled, resetOnDisabled, template, resolveWarning, reset]);

  // Resolve the template (or restore the saved snapshot) whenever the slot is
  // enabled and has no state yet. Gated on !template/!loading/!failed so a
  // settled slot stays settled until a reset re-opens the gate.
  useEffect(() => {
    const initTemplate = async () => {
      // Tag this call so a slower, now-superseded response (from params the
      // user has since changed away from) can't overwrite newer state.
      const requestId = ++requestIdRef.current;
      const isStale = () => requestIdRef.current !== requestId;

      setLoading(true);
      setFailed(false);
      setResolveWarning(null);

      // Saved snapshot restore (edit flows). Skipped once the params have been
      // manually changed when configured - at that point the user wants a fresh
      // resolve for the new type, not the snapshot saved under the old one.
      if (loadSaved && !(skipSavedAfterParamsChange && manualChangeRef.current)) {
        let saved = null;
        try {
          saved = await loadSaved();
        } catch (err) {
          console.error(`[${logLabel}] Error loading saved check:`, err);
          // Continue to /resolve as fallback
        }
        if (isStale()) return;
        if (saved) {
          setTemplate(saved.template);
          if (saved.metadata) setMetadata(saved.metadata);
          setAnswers(saved.answers || {});
          setAttachments(saved.attachments || {});
          if (saved.savedCheck) setSavedCheck(saved.savedCheck);
          if (typeof saved.completed === 'boolean') setCompleted(saved.completed);
          setLoading(false);
          return;
        }
      }

      // For new checks, use the /resolve endpoint with targeting parameters.
      const resolveUrlFor = (targeting) => `/api/v1/check-form-templates/resolve?${new URLSearchParams(targeting).toString()}`;
      // The legacy hard-coded fallback chain only applies to Site Work.
      const fallbackChainFor = (targeting) => Boolean(fallback?.formType && targeting?.workLogType === 'site_work');
      // The targeting the outcome below belongs to: resolveParams, or
      // resolveFallbackParams once the first lookup found nothing.
      let activeParams = resolveParams;
      let useFallbackChain = fallbackChainFor(activeParams);

      let resolveSucceeded = false;
      let pendingWarning = null;

      try {
        console.log(`[${logLabel}] Resolving template with targeting:`, resolveParams);
        let resolveResponse = await authFetch(resolveUrlFor(resolveParams));

        // A newer request has since started - drop this response instead of
        // letting it overwrite the newer, in-flight state.
        if (isStale()) return;

        // Nothing set up for the first targeting: look again with the second
        // before treating it as missing. A 409 is never retried - an
        // ambiguous first match must still be shown and block completion.
        if (resolveResponse.status === 404 && resolveFallbackParams) {
          console.log(`[${logLabel}] /resolve 404 - resolving again with:`, resolveFallbackParams);
          activeParams = resolveFallbackParams;
          useFallbackChain = fallbackChainFor(activeParams);
          resolveResponse = await authFetch(resolveUrlFor(activeParams));
          if (isStale()) return;
        }

        if (resolveResponse.ok) {
          const resolvedTemplate = await resolveResponse.json();
          if (isStale()) return;
          setTemplate(resolvedTemplate);
          setMetadata({
            template_id: resolvedTemplate.template_id,
            template_version: resolvedTemplate.version,
            template_snapshot: resolvedTemplate.schema_json
          });
          setAnswers({});
          setUsedResolveFallback(activeParams !== resolveParams);
          resolveSucceeded = true;
          console.log(`[${logLabel}] Resolved template:`, resolvedTemplate.name, 'v' + resolvedTemplate.version);
        } else if (resolveResponse.status === 404) {
          const errorData = await resolveResponse.json().catch(() => ({}));
          if (isStale()) return;
          if (useFallbackChain) {
            pendingWarning = `No template found for targeting (category=${activeParams.category}, workLogType=${activeParams.workLogType}, assetType=${activeParams.assetType}). Using fallback.`;
            console.warn(`[${logLabel}] /resolve 404:`, errorData.message || 'No matching template');
          } else {
            // No matching template - no check required, completion not blocked.
            console.log(`[${logLabel}] /resolve 404 - no matching template, no check required`);
            setFailed(true);
          }
        } else if (resolveResponse.status === 409) {
          const errorData = await resolveResponse.json().catch(() => ({}));
          if (isStale()) return;
          if (useFallbackChain) {
            const conflictNames = errorData.conflicting_templates?.map(t => t.name).join(', ') || 'multiple templates';
            pendingWarning = `Targeting ambiguity: multiple templates match (${conflictNames}). Using fallback.`;
            console.warn(`[${logLabel}] /resolve 409:`, errorData.message || 'Multiple matching templates');
          } else {
            setResolveWarning(conflictMessage ? conflictMessage(errorData, activeParams) : `Targeting ambiguity: multiple templates match. Contact admin to fix template targeting.`);
            setFailed(true);
            console.warn(`[${logLabel}] /resolve 409 - ambiguous:`, errorData.message);
          }
        } else {
          if (useFallbackChain) {
            pendingWarning = `Template resolution failed (status ${resolveResponse.status}). Using fallback.`;
          } else {
            setFailed(true);
          }
          console.warn(`[${logLabel}] /resolve unexpected status:`, resolveResponse.status);
        }
      } catch (err) {
        if (isStale()) return;
        if (useFallbackChain) {
          pendingWarning = `Template resolution error: ${err.message}. Using fallback.`;
        } else {
          setFailed(true);
        }
        console.error(`[${logLabel}] /resolve error:`, err);
      }

      if (!useFallbackChain || resolveSucceeded) {
        setLoading(false);
        return;
      }

      // Site Work only: /resolve failed, fall back to /active/:formType and
      // finally to the caller's hard-coded field list (caller renders it when
      // the slot ends up failed / templateless).
      try {
        console.log(`[${logLabel}] Falling back to /active/${fallback.formType}`);
        const fallbackResponse = await authFetch(`/api/v1/check-form-templates/active/${fallback.formType}`);

        if (isStale()) return;

        if (fallbackResponse.ok) {
          const fallbackTemplate = await fallbackResponse.json();
          if (isStale()) return;
          setTemplate(fallbackTemplate);
          setMetadata({
            template_id: fallbackTemplate.template_id,
            template_version: fallbackTemplate.version,
            template_snapshot: fallbackTemplate.schema_json
          });
          setAnswers({});
          setResolveWarning(pendingWarning);
          console.log(`[${logLabel}] Fallback succeeded:`, fallbackTemplate.name, 'v' + fallbackTemplate.version);
        } else if (fallbackResponse.status === 409) {
          const errorData = await fallbackResponse.json().catch(() => ({}));
          if (isStale()) return;
          setResolveWarning(
            fallbackConflictMessage
              ? fallbackConflictMessage(errorData)
              : `Multiple active templates exist. An admin needs to configure targeting in Settings > Check Forms. Using hard-coded fallback form.`
          );
          setFailed(true);
          console.warn(`[${logLabel}] Fallback 409 - ambiguous lookup:`, errorData.message);
        } else {
          console.error(`[${logLabel}] Fallback also failed, status:`, fallbackResponse.status);
          setFailed(true);
        }
      } catch (err) {
        if (isStale()) return;
        console.error(`[${logLabel}] Fallback error:`, err);
        setFailed(true);
      }

      setLoading(false);
    };

    if (enabled && !template && !loading && !failed) {
      initTemplate();
    }
  }, [
    enabled, template, loading, failed,
    authFetch, resolveParams, resolveFallbackParams, loadSaved, skipSavedAfterParamsChange,
    fallback, conflictMessage, fallbackConflictMessage, logLabel
  ]);

  // The latest attachments, so a removal can name the photo it removes
  // without re-creating removeAttachment on every change.
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;

  const addAttachments = useCallback(async (stepKey, files, inputElement) => {
    if (uploadAttachment) {
      // Copy the list first: clearing the picker below empties a live FileList.
      const picked = Array.from(files || []);
      if (inputElement) inputElement.value = '';
      const errors = await uploadFilesOneByOne(picked, file => uploadAttachment(stepKey, file), ref => {
        const attachment = attachmentFromFileRef(ref, stepKey);
        setAttachments(prev => ({
          ...prev,
          [stepKey]: [...(prev[stepKey] || []), attachment]
        }));
      });
      if (errors.length > 0 && onError) onError(errors.join('; '));
      return;
    }
    try {
      const newAttachments = await filesToAttachmentPayload(files);
      const attachmentsWithStep = newAttachments.map(att => ({ ...att, step_key: stepKey }));
      setAttachments(prev => ({
        ...prev,
        [stepKey]: [
          ...(prev[stepKey] || []),
          ...attachmentsWithStep
        ]
      }));
    } catch (err) {
      if (onError) onError(err.message);
    } finally {
      if (inputElement) inputElement.value = '';
    }
  }, [onError, uploadAttachment]);

  // Held in refs so removeAttachment stays the same function it always was.
  const removeUploadedRef = useRef(removeUploadedAttachment);
  removeUploadedRef.current = removeUploadedAttachment;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const removeAttachment = useCallback((stepKey, index) => {
    const removed = (attachmentsRef.current[stepKey] || [])[index];
    const removeSent = removeUploadedRef.current;
    if (removeSent && removed && (removed.file_id || removed.client_ref)) {
      removeSentAttachment(removeSent, removed, message => {
        if (onErrorRef.current) onErrorRef.current(message);
      });
    }
    setAttachments(prev => {
      const currentAttachments = prev[stepKey] || [];
      return {
        ...prev,
        [stepKey]: currentAttachments.filter((_, i) => i !== index)
      };
    });
  }, []);

  /** Flattens step-keyed attachments to the array shape the save endpoints expect. */
  const buildAttachmentsArray = useCallback(() => {
    const allAttachments = [];
    Object.entries(attachments || {}).forEach(([stepKey, stepAttachments]) => {
      stepAttachments.forEach(att => {
        allAttachments.push({ ...att, step_key: stepKey });
      });
    });
    return allAttachments;
  }, [attachments]);

  /**
   * check_data for the brush/spray robot-check payloads: caller-supplied base
   * fields plus the template metadata + answers.
   */
  const buildCheckData = useCallback((base = {}) => ({
    ...base,
    template_id: metadata?.template_id,
    template_version: metadata?.template_version,
    template_snapshot: metadata?.template_snapshot,
    template_answers: answers
  }), [metadata, answers]);

  /** A template_checks[] entry (generic checks / task work logs / maintenance sign-off). */
  const buildTemplateCheckEntry = useCallback((assetId) => ({
    template_id: template?.template_id,
    asset_id: assetId || null,
    template_answers: answers,
    attachments: buildAttachmentsArray(),
    check_completed: completed
  }), [template, answers, buildAttachmentsArray, completed]);

  return {
    template,
    metadata,
    answers,
    setAnswers,
    attachments,
    // Saved Log Work: lets a caller fill in a sent photo's url by client_ref.
    setAttachments,
    addAttachments,
    removeAttachment,
    resolveWarning,
    failed,
    loading,
    completed,
    setCompleted,
    savedCheck,
    // True when the template on show came from resolveFallbackParams.
    usedResolveFallback,
    // A warning with no usable template blocks completion at the call sites.
    completionBlocked: Boolean(resolveWarning && !template && !loading),
    reset,
    buildAttachmentsArray,
    buildCheckData,
    buildTemplateCheckEntry
  };
}

export default useTemplateCheckForm;
